import { useEffect, useState, useMemo } from 'react';
import drupalPubSubService from '../../services/drupalPubSub';
import { stringToCentAmount } from '../../utils';
import ABTesting from '../../utils/ab-testing';
import { parseDrupalSettingsVariants } from '../../utils/variants';

const useDrupalSettings = () => {
  const [drupalSettingsVersion, setDrupalSettingsVersion] = useState(1);

  useEffect(() => {
    drupalPubSubService.subscribe({
      publishedEvent: 'update-drupalSettings',
      callback: () => {
        setDrupalSettingsVersion((previousVersion) => previousVersion + 1);
      },
    });
  }, []);

  const processedSettings = useMemo(() => {
    // eslint-disable-next-line no-undef
    const settings = window?.drupalSettings;

    const filteredAvos = ABTesting.filterAovsByTestId(settings?.aovs);
    const mergedAovs = ABTesting.formatMergedAovs(filteredAvos);
    const abTestingGroup = ABTesting.getABTestingGroup(true);
    return {
      ...settings,
      rioMappingTable: settings?.rio?.map || {},
      promotionalBanner: settings?.promotionalBanner ? {
        ...settings?.promotionalBanner,
      } : undefined,
      aovs: mergedAovs || [],
      product: {
        ...settings?.product,
        variants: parseDrupalSettingsVariants(settings?.product?.variants),
      },
      bedframeSelector: settings?.bedframeSelector
        ? {
            ...settings.bedframeSelector,
            productParts: settings.bedframeSelector.productParts?.map(
              (productPart) => ({
                ...productPart,
                name: productPart.alternativeName ?? productPart.name,
                description:
                  productPart.alternativeDescription ?? productPart.description,
                variants: productPart.variants?.map((variant) => {
                  const abTestingObject = variant.pt?.[abTestingGroup];

                  const priceToParse = abTestingObject
                    ? abTestingObject.price
                    : variant.price;
                  const discountToParse = abTestingObject
                    ? abTestingObject.discount
                    : variant.discount;

                  const discount = stringToCentAmount({
                    value: discountToParse,
                  });
                  const basePrice = stringToCentAmount({ value: priceToParse });

                  const discountedPrice = {
                    ...basePrice,
                    centAmount: basePrice.centAmount - discount.centAmount,
                  };
                  return {
                    ...variant,
                    price: discountedPrice,
                    discount,
                  };
                }),
              }),
            ),
          }
        : null,
      bundle: settings?.bundle?.map((bundleItem) => ({
        ...bundleItem,
        product: {
          ...bundleItem.product,
          variants: bundleItem.product.variants.map((variant) => {
            const discount = stringToCentAmount({ value: variant.discount });
            const basePrice = stringToCentAmount({ value: variant.price });
            const discountedPrice = {
              ...basePrice,
              centAmount: basePrice.centAmount - discount.centAmount,
            };
            return {
              ...variant,
              price: discountedPrice,
              discount,
            };
          }),
        },
      })),
      headboardAddOn: settings?.headboardAddOn,
      featureToggle: settings?.feature_toggle?.enabled ?? [],
      infoBlock: settings?.infoBlock,
      truemed: {
        skus: settings?.truemed?.valid_skus ?? []
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drupalSettingsVersion]);
  return processedSettings;
};

export default useDrupalSettings;
